// @system — Blog post card sub-components (grid PostCard + FeaturedPost).
// Ships in the main bundle with BlogPage — keep it lean (no new dependencies here).
import { Link } from 'react-router-dom'
import { Calendar, Clock, ArrowRight } from 'lucide-react'
import { Card, CardContent } from '../../../../components/@system/Card'
import { text } from '@/config'
import { formatDate } from './helpers'
import { CoverPlaceholder } from './CoverPlaceholder'

const bt = text.blog

/** @typedef {{ post: import('./helpers').BlogPost }} PostProps */

/**
 * @param {string} template  e.g. '{minutes} min read'
 * @param {Record<string, string | number>} values
 */
function tpl(template, values) {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(values[k] ?? ''))
}

/**
 * Card/hero image: the stored thumbnail (640×360) or cover, else the branded placeholder.
 * `alt=""` — the title sits right next to it inside the same link, so the image is decorative.
 * @param {{ post: import('./helpers').BlogPost, large?: boolean, className?: string }} props
 */
function CardImage({ post, large = false, className }) {
  const src = large ? (post.coverImage || post.thumbnailImage) : (post.thumbnailImage || post.coverImage)
  if (!src) return <CoverPlaceholder label={post.category} className={className} />
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      width={large ? 1600 : 640}
      height={large ? 900 : 360}
      className={`aspect-video w-full object-cover ${className ?? ''}`}
    />
  )
}

/** @param {{ minutes: number | null | undefined }} props */
function ReadingTime({ minutes }) {
  if (typeof minutes !== 'number' || minutes <= 0) return null
  return (
    <span className="flex items-center gap-1">
      <Clock className="h-3 w-3" aria-hidden="true" />
      {tpl(bt.minRead, { minutes })}
    </span>
  )
}

/** @param {PostProps} props */
export function PostCard({ post }) {
  return (
    <Link to={`/blog/${post.slug}`} className="group block h-full">
      <Card className="h-full overflow-hidden transition-shadow group-hover:shadow-md group-hover:border-primary/40">
        <CardImage post={post} className="rounded-t-lg" />
        <CardContent className="pt-4 pb-5 flex flex-col gap-3 h-auto">
          {/* Category + date */}
          <div className="flex items-center justify-between gap-2 text-xs text-brand-text-muted">
            <span className="rounded-full bg-brand-primary/10 text-brand-primary px-2.5 py-0.5 font-medium">
              {post.category}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" aria-hidden="true" />
              {formatDate(post.publishedAt)}
            </span>
          </div>

          {/* Title */}
          <h2 className="text-base font-semibold leading-snug group-hover:text-brand-primary transition-colors line-clamp-2">
            {post.title}
          </h2>

          {/* Excerpt */}
          <p className="text-sm text-brand-text-muted leading-relaxed line-clamp-3 flex-1">
            {post.excerpt}
          </p>

          {/* Footer */}
          <div className="flex items-center justify-between gap-2 mt-1 text-xs text-brand-text-muted">
            <span>{post.author}</span>
            <ReadingTime minutes={post.readingTime} />
          </div>

          {/* Read more */}
          <div className="flex items-center gap-1 text-xs text-brand-primary font-medium mt-auto pt-1">
            {bt.readMore} <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

/** @param {PostProps} props */
export function FeaturedPost({ post }) {
  return (
    <Link to={`/blog/${post.slug}`} className="group block">
      <Card className="overflow-hidden transition-shadow group-hover:shadow-md group-hover:border-primary/40">
        <div className="grid gap-0 md:grid-cols-2">
          <CardImage post={post} large className="h-full rounded-t-lg md:rounded-l-lg md:rounded-tr-none" />
          <CardContent className="pt-6 pb-6 flex flex-col gap-4 justify-center">
            <div className="flex flex-wrap items-center gap-3 text-xs text-brand-text-muted">
              <span className="rounded-full bg-brand-primary/10 text-brand-primary px-2.5 py-0.5 font-medium">
                {post.category}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" aria-hidden="true" />
                {formatDate(post.publishedAt)}
              </span>
              <ReadingTime minutes={post.readingTime} />
            </div>

            <h2 className="text-xl sm:text-2xl font-bold leading-snug group-hover:text-brand-primary transition-colors">
              {post.title}
            </h2>

            <p className="text-brand-text-muted leading-relaxed line-clamp-4">{post.excerpt}</p>

            <div className="flex items-center justify-between gap-2 mt-2">
              <span className="text-sm text-brand-text-muted">{post.author}</span>
              <div className="flex items-center gap-1 text-sm text-brand-primary font-medium">
                {bt.readArticle} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </div>
            </div>
          </CardContent>
        </div>
      </Card>
    </Link>
  )
}
